import { useEffect, useState, useCallback } from "react";
import { Wrench, Loader2, Plus, Pencil, Archive, X, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6", GREEN = "#1E7E45";

const STATUS_COLOR = {
  active: { color: GREEN, bg: "rgba(30,126,69,.10)" },
  inactive: { color: SLATE, bg: "rgba(119,127,136,.12)" },
};
const emptyForm = { name: "", service_type: "", description: "", status: "active", location_id: "" };

export default function Services() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myRank, setMyRank] = useState(0);
  const [locs, setLocs] = useState([]);
  const [multi, setMulti] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const canManage = myRank >= 3;
  const locName = (id) => (id ? (locs.find((l) => l.id === id)?.name || t("common.location")) : t("common.all_locations"));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/services").then(({ data }) => setItems(data.services || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/org/current").then(({ data }) => { if (data.status === "ok") setMyRank({ owner: 4, admin: 3, editor: 2, employee: 1 }[data.tenant.role] || 0); }).catch(() => {});
    api.get("/me/locations").then(({ data }) => { setLocs(data.locations || []); setMulti(!!data.multi); }).catch(() => {});
  }, [load]);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (s) => { setForm({ name: s.name || "", service_type: s.service_type || "", description: s.description || "", status: s.status || "active", location_id: s.location_id || "" }); setEditing(s.id); };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("services.err_name")); return; }
    setSaving(true);
    const payload = { name: form.name.trim(), service_type: form.service_type, description: form.description, status: form.status };
    if (multi) payload.location_id = form.location_id || null;
    try {
      if (editing === "new") { await api.post("/services", payload); toast.success(t("services.toast_created")); }
      else { await api.put(`/services/${editing}`, payload); toast.success(t("services.toast_updated")); }
      close(); load();
    } catch (e2) { toast.error(e2.response?.data?.detail || t("services.err_save")); }
    finally { setSaving(false); }
  };

  const archive = async (s) => {
    if (!window.confirm(t("services.archive_confirm", { name: s.name }))) return;
    try { await api.delete(`/services/${s.id}`); toast.success(t("services.toast_archived")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("services.err")); }
  };

  return (
    <CockpitLayout active="services" title={t("services.title")}>
      <div className="fundio-page-stack" data-testid="gold-services">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" style={{ color: INK }} data-testid="services-title">
              <Wrench size={28} style={{ color: GOLD }} /> {t("services.title")}
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">{t("services.subtitle")}</p>
          </div>
          {canManage && (
            <button data-testid="service-new-btn" onClick={openNew} className="fundio-action fundio-action--gold">
              <Plus size={16} /> {t("services.add")}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : items.length === 0 ? (
          <div className="fundio-panel p-10 text-center" style={{ borderStyle: "dashed" }} data-testid="services-empty">
            <Wrench size={30} className="mx-auto" style={{ color: LINE }} />
            <p className="mt-3 text-[15px] font-semibold" style={{ color: INK }}>{t("services.empty_title")}</p>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>{t("services.empty_body")}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="services-list">
            {items.map((s) => {
              const st = STATUS_COLOR[s.status] || STATUS_COLOR.inactive;
              return (
                <div key={s.id} className="fundio-panel" data-testid={`service-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-[16px] font-bold" style={{ color: INK }}>{s.name}</h3>
                      {s.service_type && <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "rgba(216,154,0,.12)", color: GOLD }}>{s.service_type}</span>}
                    </div>
                    <span className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase" style={{ background: st.bg, color: st.color }} data-testid={`service-status-${s.id}`}>{t(`services.status_${s.status}`)}</span>
                  </div>
                  {s.description && <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: MUTED }}>{s.description}</p>}
                  {multi && <p className="mt-3 flex items-center gap-1 text-[12px]" style={{ color: SLATE }}><MapPin size={13} /> {locName(s.location_id)}</p>}
                  {canManage && (
                    <div className="mt-4 flex items-center gap-2 border-t pt-3" style={{ borderColor: LINE }}>
                      <button data-testid={`service-edit-${s.id}`} onClick={() => openEdit(s)} className="fundio-action" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13 }}><Pencil size={14} /> {t("services.edit")}</button>
                      <button data-testid={`service-archive-${s.id}`} onClick={() => archive(s)} className="fundio-action" style={{ minHeight: 34, padding: "6px 12px", fontSize: 13, color: "#903824", borderColor: "#E7B9AF" }}><Archive size={14} /> {t("services.archive")}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" data-testid="service-editor">
          <div className="fundio-panel w-full max-w-lg rounded-t-2xl sm:rounded-2xl" style={{ maxHeight: "92vh", overflowY: "auto" }}>
            <div className="flex items-center justify-between">
              <h2 className="text-[18px] font-bold" style={{ color: INK }}>{editing === "new" ? t("services.form_add") : t("services.form_edit")}</h2>
              <button onClick={close} data-testid="service-editor-close" className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-fundio-subtle" style={{ color: SLATE }}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="mt-4 space-y-3">
              <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("services.name")}</label>
                <input data-testid="service-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fundio-input" placeholder={t("services.name_ph")} /></div>
              <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("services.type")}</label>
                <input data-testid="service-form-type" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className="fundio-input" placeholder={t("services.type_ph")} /></div>
              <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("services.desc")}</label>
                <textarea data-testid="service-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="fundio-input" placeholder={t("services.desc_ph")} /></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("services.status")}</label>
                  <select data-testid="service-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="fundio-input">
                    <option value="active">{t("services.status_active")}</option><option value="inactive">{t("services.status_inactive")}</option>
                  </select></div>
                {multi && (
                  <div><label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>{t("common.location")}</label>
                    <select data-testid="service-form-location" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="fundio-input">
                      <option value="">{t("common.all_locations")}</option>
                      {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select></div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={close} className="fundio-action">{t("common.cancel")}</button>
                <button type="submit" data-testid="service-form-submit" disabled={saving} className="fundio-action fundio-action--gold">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : null} {t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CockpitLayout>
  );
}
